/**
 * Pricing Service
 * Handles fetching and processing pricing data from the API
 */

import { environment } from '../environments/environment.js';
import type {
  PricingResponse,
  Timeframe,
  ChartSeries,
  SeriesName,
  ChartDataPoint,
} from '../types/pricing.js';

/**
 * Singleton service for pricing data
 */
class PricingService {
  private static instance: PricingService;
  private readonly baseUrl = `${environment.api}/${environment.endpoints.pricing}`;
  private cache: Map<Timeframe, PricingResponse> = new Map();

  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): PricingService {
    if (!PricingService.instance) {
      PricingService.instance = new PricingService();
    }
    return PricingService.instance;
  }

  /**
   * Fetch pricing data from API
   */
  async fetchPricing(
    timeframe: Timeframe = 'd'
  ): Promise<PricingResponse> {
    // Check cache first
    const cached = this.cache.get(timeframe);
    if (cached) {
      return cached;
    }

    try {
      const url = new URL(this.baseUrl);
      url.searchParams.set('timeframe', timeframe);

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(
          `Failed to fetch pricing data: ${response.status} ${response.statusText}`
        );
      }

      const data: PricingResponse = await response.json();

      // Validate response structure
      if (!data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid response format: missing data array');
      }

      // Cache the result
      this.cache.set(timeframe, data);

      return data;
    } catch (error) {
      console.error('Error fetching pricing data:', error);
      throw error;
    }
  }

  /**
   * Transform API data to chart series format
   */
  transformToChartSeries(
    response: PricingResponse,
    selectedSeries: SeriesName[]
  ): ChartSeries[] {
    const seriesMap = new Map<SeriesName, ChartDataPoint[]>();

    // Initialize empty arrays for each selected series
    selectedSeries.forEach((name) => {
      seriesMap.set(name, []);
    });

    // Process each data point
    response.data.forEach((point) => {
      const timestamp = this.parseTimestamp(point.timestamp);

      selectedSeries.forEach((seriesName) => {
        const value = point.series[seriesName];

        // Only add non-null values
        if (value !== null) {
          seriesMap.get(seriesName)!.push({
            time: timestamp,
            value,
          });
        }
      });
    });

    // Convert to ChartSeries array (pure data, no presentation)
    return selectedSeries
      .map((name) => ({
        name,
        data: seriesMap.get(name)!,
      }))
      .filter((series) => series.data.length > 0); // Only include series with data
  }

  /**
   * Parse ISO timestamp to Unix timestamp in seconds
   */
  private parseTimestamp(timestamp: string): number {
    return Math.floor(new Date(timestamp).getTime() / 1000);
  }

  /**
   * Clear cache (useful for forcing refresh)
   */
  clearCache(): void {
    this.cache.clear();
  }
}

// Export singleton instance
export const pricingService = PricingService.getInstance();
